// src/pages/Jobs/JobMap.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button } from 'reactstrap';
import { RefreshCw } from 'lucide-react';
import { Job } from '../../models/Job.interface';

interface JobMapProps {
  jobs: Job[];
  center?: {
    latitude: number;
    longitude: number;
  };
  zoom?: number;
  highlightedJobId?: string;
  onJobClick?: (job: Job) => void;
  onJobHover?: (job: Job | null) => void;
  onBoundsChange?: (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => void;
  onRequeryListings?: (center: { latitude: number; longitude: number }, bounds?: { north: number; south: number; east: number; west: number }) => void;
  isRequerying?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const JobMap: React.FC<JobMapProps> = ({
  jobs,
  center = { latitude: 39.8283, longitude: -98.5795 },
  zoom = 6,
  highlightedJobId,
  onJobClick,
  onJobHover,
  onBoundsChange,
  onRequeryListings,
  isRequerying = false,
  className = '',
  style = {}
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const boundsChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousJobsRef = useRef<string>('');
  const hasFittedBoundsRef = useRef<boolean>(false);
  const [showRequeryButton, setShowRequeryButton] = useState(false);
  const [isLocalRequerying, setIsLocalRequerying] = useState(false);
  const isRequeryingRef = useRef(false);

  // Helper function to validate coordinates
  const isValidCoordinate = (lat: any, lng: any): boolean => {
    const latNum = typeof lat === 'string' ? parseFloat(lat) : lat;
    const lngNum = typeof lng === 'string' ? parseFloat(lng) : lng;

    return latNum != null &&
      lngNum != null &&
      !isNaN(latNum) &&
      !isNaN(lngNum) &&
      isFinite(latNum) &&
      isFinite(lngNum) &&
      latNum >= -90 &&
      latNum <= 90 &&
      lngNum >= -180 &&
      lngNum <= 180;
  };

  // Helper function to safely convert coordinates to numbers
  const getCoordinateNumbers = (lat: any, lng: any): { lat: number; lng: number } => {
    const latNum = typeof lat === 'string' ? parseFloat(lat) : lat;
    const lngNum = typeof lng === 'string' ? parseFloat(lng) : lng;
    return { lat: latNum, lng: lngNum };
  };

  // Show info window for a job
  const showInfoWindow = (job: Job, marker: google.maps.Marker) => {
    if (!infoWindowRef.current) return;

    const formatAddress = () => {
      const { propertyAddress } = job;
      if (!propertyAddress) return 'Location not specified';
      return `${propertyAddress.address1} ${propertyAddress.city}, ${propertyAddress.state} ${propertyAddress.zipCode}`;
    };

    const content = `
      <div style="max-width: 300px; padding: 0px 6px 12px 6px;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px;">
          <h6 style="margin: 0; font-weight: 600; flex: 1; margin-right: 8px;">${job.jobTitle}</h6>
          <span class="badge bg-${getStatusBadgeColor(job.jobStatus)} text-white" style="font-size: 0.7em; padding: 4px 8px; border-radius: 12px; white-space: nowrap;">
            ${job.jobStatus}
          </span>
        </div>
        
        <div style="color: #6c757d; font-size: 0.9em; margin-bottom: 8px; display: flex; align-items: center;">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 4px;">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
          ${formatAddress()}
        </div>
        
        ${job.jobCategories && job.jobCategories.length > 0 ? `
          <div style="margin-bottom: 8px;">
            ${job.jobCategories.slice(0, 2).map(category =>
              `<span style="background: #f8f9fa; color: #495057; padding: 2px 6px; border-radius: 6px; font-size: 0.8em; margin-right: 4px; display: inline-block; margin-bottom: 2px;">${category}</span>`
            ).join('')}
            ${job.jobCategories.length > 2 ? `<span style="color: #6c757d; font-size: 0.8em;">+${job.jobCategories.length - 2} more</span>` : ''}
          </div>
        ` : ''}
        
        <p style="margin: 8px 0; font-size: 0.9em; color: #495057; line-height: 1.4; max-height: 60px; overflow: hidden;">
          ${job.jobSummary || job.jobDescription || 'No description available'}
        </p>
        
        <div style="margin-top: 10px; margin-bottom: 0;">
          <a
            href="/job/${job.seoUrl || 'job'}/${job.id}"
            target="_blank"
            rel="noopener noreferrer"
            style="color: #0d6efd; text-decoration: none; font-size: 0.9em; font-weight: 500;"
          >
            View Details →
          </a>
        </div>
      </div>
    `;

    infoWindowRef.current.setContent(content);
    infoWindowRef.current.open(googleMapRef.current, marker);
  };

  // Handle requery button click
  const handleRequeryClick = async () => {
    if (!googleMapRef.current || !onRequeryListings) return;

    setIsLocalRequerying(true);
    isRequeryingRef.current = true; // Set ref to prevent bounds fitting
    
    const mapCenter = googleMapRef.current.getCenter();
    const mapBounds = googleMapRef.current.getBounds();
    
    if (mapCenter && mapBounds) {
      const newCenter = {
        latitude: mapCenter.lat(),
        longitude: mapCenter.lng()
      };
      
      const bounds = {
        north: mapBounds.getNorthEast().lat(),
        south: mapBounds.getSouthWest().lat(),
        east: mapBounds.getNorthEast().lng(),
        west: mapBounds.getSouthWest().lng()
      };
      
      console.log('🗺️ JobMap: Sending requery with center and bounds:', { newCenter, bounds });
      
      try {
        await onRequeryListings(newCenter, bounds);
        setShowRequeryButton(false);
      } catch (error) {
        console.error('Error requerying listings:', error);
      }
    }
    
    setIsLocalRequerying(false);
    // Keep the ref true briefly to prevent bounds fitting on the incoming job updates
    setTimeout(() => {
      isRequeryingRef.current = false;
    }, 1000);
  };

  // Initialize Google Map
  useEffect(() => {
    const initMap = () => {
      if (!mapRef.current || !window.google) return;

      console.log('🗺️ JobMap: Initializing map with center:', center);

      const validCenter = isValidCoordinate(center.latitude, center.longitude)
        ? { lat: center.latitude, lng: center.longitude }
        : { lat: 39.8283, lng: -98.5795 };

      console.log('🗺️ JobMap: Using center coordinates:', validCenter);

      const map = new window.google.maps.Map(mapRef.current, {
        center: validCenter,
        zoom: zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      googleMapRef.current = map;
      infoWindowRef.current = new window.google.maps.InfoWindow();

      // Add bounds change listener with debouncing
      if (onBoundsChange) {
        const handleBoundsChange = () => {
          if (boundsChangeTimeoutRef.current) {
            clearTimeout(boundsChangeTimeoutRef.current);
          }

          boundsChangeTimeoutRef.current = setTimeout(() => {
            const bounds = map.getBounds();
            if (bounds) {
              const ne = bounds.getNorthEast();
              const sw = bounds.getSouthWest();
              onBoundsChange({
                north: ne.lat(),
                south: sw.lat(),
                east: ne.lng(),
                west: sw.lng()
              });
            }
          }, 500);
        };

        map.addListener('bounds_changed', handleBoundsChange);
      }

      // Add listeners to show requery button when map is moved/zoomed
      if (onRequeryListings) {
        const showButton = () => {
          if (hasFittedBoundsRef.current) {
            setShowRequeryButton(true);
          }
        };

        map.addListener('dragend', showButton);
        map.addListener('zoom_changed', showButton);
      }

      setIsLoaded(true);
    };

    if (window.google && window.google.maps) {
      initMap();
    } else {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.onload = initMap;
      document.head.appendChild(script);
    }

    return () => {
      if (boundsChangeTimeoutRef.current) {
        clearTimeout(boundsChangeTimeoutRef.current);
      }
    };
  }, [center.latitude, center.longitude, zoom]);

  // Update map center when center prop changes (but not during requery)
  useEffect(() => {
    if (!googleMapRef.current || !isLoaded || isRequerying || isRequeryingRef.current) return;

    console.log('🗺️ JobMap: Updating map center to:', center);

    if (isValidCoordinate(center.latitude, center.longitude)) {
      googleMapRef.current.setCenter({
        lat: center.latitude,
        lng: center.longitude
      });
      console.log('🗺️ JobMap: Map center updated to:', { lat: center.latitude, lng: center.longitude });
    }
  }, [center.latitude, center.longitude, isLoaded, isRequerying]);

  // Update markers when jobs change
  useEffect(() => {
    if (!googleMapRef.current || !isLoaded) return;

    const currentJobIds = jobs.map(job => job.id).sort().join(',');

    if (currentJobIds === previousJobsRef.current) {
      console.log('🗺️ JobMap: Skipping update - jobs unchanged');
      return;
    }

    previousJobsRef.current = currentJobIds;
    console.log('🗺️ JobMap: Updating markers for', jobs.length, 'jobs');

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current.clear();

    let validJobCount = 0;
    let invalidJobCount = 0;

    // Create markers for each job
    jobs.forEach(job => {
      const lat = job.propertyAddress?.latitude;
      const lng = job.propertyAddress?.longitude;

      if (!isValidCoordinate(lat, lng)) {
        console.warn(`🗺️ JobMap: Skipping job ${job.id} - invalid coordinates:`, { lat, lng });
        invalidJobCount++;
        return;
      }

      validJobCount++;
      const { lat: latNum, lng: lngNum } = getCoordinateNumbers(lat, lng);

      const marker = new window.google.maps.Marker({
        position: { lat: latNum, lng: lngNum },
        map: googleMapRef.current,
        title: job.jobTitle,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <circle cx="16" cy="16" r="12" fill="${getJobStatusColor(job.jobStatus)}" stroke="white" stroke-width="2"/>
              <circle cx="16" cy="16" r="6" fill="white"/>
              <text x="16" y="20" text-anchor="middle" font-size="10" font-weight="bold" fill="${getJobStatusColor(job.jobStatus)}">J</text>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(32, 32),
          anchor: new window.google.maps.Point(16, 16)
        }
      });

      marker.addListener('click', () => {
        onJobClick?.(job);
        showInfoWindow(job, marker);
      });

      marker.addListener('mouseover', () => {
        showInfoWindow(job, marker);
      });

      markersRef.current.set(job.id, marker);
    });

    console.log(`🗺️ JobMap: Created ${validJobCount} markers, skipped ${invalidJobCount} invalid jobs`);

    const jobsWithValidCoords = jobs.filter(job =>
      isValidCoordinate(job.propertyAddress?.latitude, job.propertyAddress?.longitude)
    );

    // Only fit bounds on the FIRST load with valid jobs, not on subsequent updates or requerying
    if (jobsWithValidCoords.length > 0 && !hasFittedBoundsRef.current && !isRequeryingRef.current) {
      console.log('🗺️ JobMap: First load - fitting bounds for', jobsWithValidCoords.length, 'jobs');

      const bounds = new window.google.maps.LatLngBounds();
      jobsWithValidCoords.forEach(job => {
        const { lat: latNum, lng: lngNum } = getCoordinateNumbers(
          job.propertyAddress?.latitude,
          job.propertyAddress?.longitude
        );
        bounds.extend({
          lat: latNum,
          lng: lngNum
        });
      });

      const tempListener = googleMapRef.current!.addListener('bounds_changed', () => { });

      googleMapRef.current!.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });

      hasFittedBoundsRef.current = true;

      setTimeout(() => {
        window.google.maps.event.removeListener(tempListener);
      }, 1000);

      console.log('🗺️ JobMap: Bounds fitted, future updates will not auto-fit');
    } else if (hasFittedBoundsRef.current) {
      console.log('🗺️ JobMap: Skipping bounds fitting - already fitted on first load');
    } else if (isRequeryingRef.current) {
      console.log('🗺️ JobMap: Skipping bounds fitting - currently requerying to maintain view');
    }
  }, [jobs, isLoaded]);

  // Highlight specific job
  useEffect(() => {
    if (!isLoaded) return;

    markersRef.current.forEach((marker, jobId) => {
      const job = jobs.find(j => j.id === jobId);
      if (!job) return;

      if (jobId === highlightedJobId) {
        // Highlight the marker
        marker.setIcon({
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
              <circle cx="20" cy="20" r="16" fill="${getJobStatusColor(job.jobStatus)}" stroke="white" stroke-width="3"/>
              <circle cx="20" cy="20" r="8" fill="white"/>
              <text x="20" y="25" text-anchor="middle" font-size="12" font-weight="bold" fill="${getJobStatusColor(job.jobStatus)}">J</text>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 20)
        });

        // Show info window for highlighted job
        showInfoWindow(job, marker);

        // Center map on highlighted job with enhanced validation
        const lat = job.propertyAddress?.latitude;
        const lng = job.propertyAddress?.longitude;
        const hasValidCoords = isValidCoordinate(lat, lng);

        if (hasValidCoords) {
          const { lat: latNum, lng: lngNum } = getCoordinateNumbers(lat, lng);
          googleMapRef.current?.panTo({
            lat: latNum,
            lng: lngNum
          });
        }
      } else {
        // Reset to normal marker
        marker.setIcon({
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <circle cx="16" cy="16" r="12" fill="${getJobStatusColor(job.jobStatus)}" stroke="white" stroke-width="2"/>
              <circle cx="16" cy="16" r="6" fill="white"/>
              <text x="16" y="20" text-anchor="middle" font-size="10" font-weight="bold" fill="${getJobStatusColor(job.jobStatus)}">J</text>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(32, 32),
          anchor: new window.google.maps.Point(16, 16)
        });
      }
    });
    
    // If no job is highlighted, close any open info window
    if (!highlightedJobId && infoWindowRef.current) {
      infoWindowRef.current.close();
    }
  }, [highlightedJobId, jobs, isLoaded]);

  const getJobStatusColor = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'draft':
        return '#6c757d';
      case 'published':
        return '#0d6efd';
      case 'hiring':
        return '#198754';
      case 'pending inspection':
        return '#ffc107';
      case 'under contract':
        return '#0dcaf0';
      case 'job in progress':
        return '#fd7e14';
      case 'complete':
        return '#198754';
      case 'cancelled':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getStatusBadgeColor = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'draft':
        return '#6c757d';
      case 'published':
        return '#0d6efd';
      case 'hiring':
        return '#198754';
      case 'pending inspection':
        return '#ffc107';
      case 'under contract':
        return '#0dcaf0';
      case 'job in progress':
        return '#fd7e14';
      case 'complete':
        return '#198754';
      case 'cancelled':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  return (
    <div className={`position-relative ${className}`} style={style}>
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
      
      {/* Requery Button */}
      {showRequeryButton && onRequeryListings && (
        <div
          className="position-absolute"
          style={{
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000
          }}
        >
          <Button
            color="primary"
            size="sm"
            onClick={handleRequeryClick}
            disabled={isLocalRequerying || isRequerying}
            className="d-flex align-items-center gap-1 shadow"
          >
            <RefreshCw size={14} className={(isLocalRequerying || isRequerying) ? 'spinning' : ''} />
            {(isLocalRequerying || isRequerying) ? 'Searching...' : 'Search this area'}
          </Button>
        </div>
      )}
      
      <style>{`
        .spinning {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default JobMap;